import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import type { AppDispatch } from "@/store"
import { toast } from "sonner"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { resetSage300Success } from "../../store/sage300ConnectorSlice"
import { resetCareers24Success } from "../../store/careers24ConnectorSlice"
import { useM365Connector } from "../../hooks/useM365Connector"
import { useSage300Connector } from "../../hooks/useSage300Connector"
import { useCareers24Connector } from "../../hooks/useCareers24Connector"
import { useSageVipConnector } from "../../hooks/useSageVipConnector"
import { resetSageVipSuccess } from "../../store/sageVipConnectorSlice"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { Plug, Calendar, Server, Briefcase, Loader2 } from "lucide-react"

const sage300Schema = z.object({
  baseUrl: z.string().url("Must be a valid URL"),
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  apiKey: z.string().min(1, "API key is required"),
})

const sageVipSchema = z.object({
  companyCode: z.string().min(1, "Company code is required"),
})
type SageVipFormValues = z.infer<typeof sageVipSchema>

type Sage300FormValues = z.infer<typeof sage300Schema>

const careers24Schema = z.object({
  apiKey: z.string().min(1, "API key is required"),
  consultantEmail: z.string().email("Must be a valid email"),
  applicationEmail: z.string().email("Must be a valid email"),
})

type Careers24FormValues = z.infer<typeof careers24Schema>

export default function IntegrationsPage() {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch<AppDispatch>()

  const {
    status,
    isLoading,
    error,
    connect,
    disconnect,
    isConnecting,
    isDisconnecting,
  } = useM365Connector()
  const isConnected = status?.isConnected ?? false

  const {
    connection: sageVipConnection,
    isLoading: sageVipIsLoading,
    isSubmitting: sageVipIsSubmitting,
    isDeleting: sageVipIsDeleting,
    error: sageVipError,
    isSuccess: sageVipIsSuccess,
    upsert: sageVipUpsert,
    disconnect: sageVipDisconnect,
  } = useSageVipConnector()

  const [sageVipIsEditing, setSageVipIsEditing] = useState(false)

  const sageVipForm = useForm<SageVipFormValues>({
    resolver: zodResolver(sageVipSchema),
    defaultValues: { companyCode: "" },
  })

  const {
    connection: sage300Connection,
    isLoading: sage300IsLoading,
    isSubmitting: sage300IsSubmitting,
    isDeleting: sage300IsDeleting,
    error: sage300Error,
    isSuccess: sage300IsSuccess,
    upsert: sage300Upsert,
    disconnect: sage300Disconnect,
  } = useSage300Connector()

  const {
    connection: careers24Connection,
    isLoading: careers24IsLoading,
    isSubmitting: careers24IsSubmitting,
    isDeleting: careers24IsDeleting,
    error: careers24Error,
    isSuccess: careers24IsSuccess,
    upsert: careers24Upsert,
    disconnect: careers24Disconnect,
  } = useCareers24Connector()

  const [sage300IsEditing, setSage300IsEditing] = useState(false)
  const [careers24IsEditing, setCareers24IsEditing] = useState(false)

  const sage300Form = useForm<Sage300FormValues>({
    resolver: zodResolver(sage300Schema),
    defaultValues: { baseUrl: "", username: "", password: "", apiKey: "" },
  })

  useEffect(() => {
    if (sage300Connection) {
      sage300Form.reset({
        baseUrl: sage300Connection.baseUrl,
        username: sage300Connection.username,
        password: sage300Connection.password,
        apiKey: sage300Connection.apiKey,
      })
    }
  }, [sage300Connection, sage300Form])

  const careers24Form = useForm<Careers24FormValues>({
    resolver: zodResolver(careers24Schema),
    defaultValues: { apiKey: "", consultantEmail: "", applicationEmail: "" },
  })

  useEffect(() => {
    if (careers24Connection) {
      careers24Form.reset({
        apiKey: careers24Connection.apiKey,
        consultantEmail: careers24Connection.consultantEmail,
        applicationEmail: careers24Connection.applicationEmail,
      })
    }
  }, [careers24Connection, careers24Form])

  useEffect(() => {
    if (sage300IsSuccess) {
      toast.success("Sage 300 connection saved.")
      setSage300IsEditing(false)
      dispatch(resetSage300Success())
    }
  }, [sage300IsSuccess, dispatch])

  useEffect(() => {
    if (sageVipConnection) {
      sageVipForm.reset({ companyCode: sageVipConnection.companyCode })
    }
  }, [sageVipConnection, sageVipForm])

  useEffect(() => {
    if (sageVipIsSuccess) {
      toast.success("Sage VIP connection saved.")
      setSageVipIsEditing(false)
      dispatch(resetSageVipSuccess())
    }
  }, [sageVipIsSuccess, dispatch])

  useEffect(() => {
    if (careers24IsSuccess) {
      toast.success("Careers24 connection saved.")
      setCareers24IsEditing(false)
      dispatch(resetCareers24Success())
    }
  }, [careers24IsSuccess, dispatch])

  useEffect(() => {
    const m365Status = searchParams.get("m365")
    if (m365Status === "connected") {
      toast.success("Microsoft 365 connected successfully.")
    } else if (m365Status === "error") {
      toast.error("Failed to connect Microsoft 365. Please try again.")
    }
  }, [dispatch, searchParams])

  function handleSage300Submit(values: Sage300FormValues) {
    sage300Upsert(values)
  }

  function handleCareers24Submit(values: Careers24FormValues) {
    careers24Upsert(values)
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect third-party services to extend Tempered Talent.
        </p>
      </div>

      <Card className="bg-card border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Plug className="w-5 h-5" />
            Microsoft 365
          </CardTitle>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4 animate-spin" />
              Loading status…
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-semibold text-foreground">
                    Outlook and Teams Calendar
                  </p>
                  <p className="text-sm text-green-700">
                    Sync scheduled interviews with your Outlook and Teams
                    calendars.
                  </p>
                  {isConnected && status?.upn && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {status.upn}
                      {status.connectedAtUtc && (
                        <>
                          {" "}
                          · Connected{" "}
                          {new Date(status.connectedAtUtc).toLocaleDateString()}
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Badge variant={isConnected ? "default" : "secondary"}>
                  {isConnected ? "Connected" : "Not connected"}
                </Badge>

                {isConnected ? (
                  <Button
                    variant="outline"
                    onClick={disconnect}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting ? "Disconnecting…" : "Disconnect"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={connect}
                    disabled={isConnecting}
                  >
                    {isConnecting ? "Redirecting…" : "Connect"}
                  </Button>
                )}
              </div>
            </div>
          )}

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      <Card className="bg-card border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Server className="w-5 h-5" />
            Sage 300
          </CardTitle>
        </CardHeader>

        <CardContent>
          {sage300IsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4 animate-spin" />
              Loading status…
            </div>
          ) : sage300Connection && !sage300IsEditing ? (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <p className="font-semibold text-foreground">
                  Connection details
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">URL: </span>
                  {sage300Connection.baseUrl}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Username: </span>
                  {sage300Connection.username}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Password: </span>
                  ••••••••
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">API key: </span>
                  ••••••••
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="default">Connected</Badge>
                <Button
                  variant="outline"
                  onClick={() => setSage300IsEditing(true)}
                  disabled={sage300IsDeleting}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await sage300Disconnect()
                    toast.success("Sage 300 connection removed.")
                  }}
                  disabled={sage300IsDeleting}
                >
                  {sage300IsDeleting ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
          ) : (
            <Form {...sage300Form}>
              <form
                onSubmit={sage300Form.handleSubmit(handleSage300Submit)}
                className="space-y-4"
              >
                <FormField
                  control={sage300Form.control}
                  name="baseUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://your-sage300-server/sage300api"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={sage300Form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter username" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={sage300Form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={sage300Form.control}
                  name="apiKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>API Key</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter API key"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={sage300IsSubmitting}>
                    {sage300IsSubmitting ? "Saving…" : "Save"}
                  </Button>
                  {sage300Connection && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        sage300Form.reset({
                          baseUrl: sage300Connection.baseUrl,
                          username: sage300Connection.username,
                          password: sage300Connection.password,
                          apiKey: sage300Connection.apiKey,
                        })
                        setSage300IsEditing(false)
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          )}

          {sage300Error && (
            <p className="mt-3 text-sm text-destructive">{sage300Error}</p>
          )}
        </CardContent>
      </Card>

      {/* Sage VIP Integration */}
      <Card className="bg-card border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Server className="w-5 h-5" />
            Sage VIP
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sageVipIsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading connection status...
            </div>
          ) : sageVipConnection && !sageVipIsEditing ? (
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted">
              <div className="space-y-1">
                <p className="font-semibold text-foreground">Connected</p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    Company Code:{" "}
                  </span>
                  {sageVipConnection.companyCode}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="default" className="bg-green-600">
                  Active
                </Badge>
                <Button
                  variant="outline"
                  onClick={() => setSageVipIsEditing(true)}
                  disabled={sageVipIsDeleting}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await sageVipDisconnect()
                    toast.success("Sage VIP connection removed")
                  }}
                  disabled={sageVipIsDeleting}
                >
                  {sageVipIsDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    "Disconnect"
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Form {...sageVipForm}>
              <form
                onSubmit={sageVipForm.handleSubmit((values) =>
                  sageVipUpsert(values),
                )}
                className="space-y-4"
              >
                <FormField
                  control={sageVipForm.control}
                  name="companyCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Code</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 001" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter your Sage VIP company code
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={sageVipIsSubmitting}>
                    {sageVipIsSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Connection"
                    )}
                  </Button>
                  {sageVipConnection && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        sageVipForm.reset({
                          companyCode: sageVipConnection.companyCode,
                        })
                        setSageVipIsEditing(false)
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          )}
          {sageVipError && (
            <p className="mt-3 text-sm text-destructive">{sageVipError}</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Careers24
          </CardTitle>
        </CardHeader>

        <CardContent>
          {careers24IsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4 animate-spin" />
              Loading status…
            </div>
          ) : careers24Connection && !careers24IsEditing ? (
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <p className="font-semibold text-foreground">
                  Connection details
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    Consultant email:{" "}
                  </span>
                  {careers24Connection.consultantEmail}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    Application email:{" "}
                  </span>
                  {careers24Connection.applicationEmail}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">API key: </span>
                  ••••••••
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="default">Connected</Badge>
                <Button
                  variant="outline"
                  onClick={() => setCareers24IsEditing(true)}
                  disabled={careers24IsDeleting}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    await careers24Disconnect()
                    toast.success("Careers24 connection removed.")
                  }}
                  disabled={careers24IsDeleting}
                >
                  {careers24IsDeleting ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
          ) : (
            <Form {...careers24Form}>
              <form
                onSubmit={careers24Form.handleSubmit(handleCareers24Submit)}
                className="space-y-4"
              >
                <FormField
                  control={careers24Form.control}
                  name="apiKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>API Key</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter API key"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={careers24Form.control}
                  name="consultantEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Consultant Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="consultant@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={careers24Form.control}
                  name="applicationEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Application Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="applications@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={careers24IsSubmitting}>
                    {careers24IsSubmitting ? "Saving…" : "Save"}
                  </Button>
                  {careers24Connection && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        careers24Form.reset({
                          apiKey: careers24Connection.apiKey,
                          consultantEmail: careers24Connection.consultantEmail,
                          applicationEmail:
                            careers24Connection.applicationEmail,
                        })
                        setCareers24IsEditing(false)
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          )}

          {careers24Error && (
            <p className="mt-3 text-sm text-destructive">{careers24Error}</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
